import { ChangeEvent, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'
import { gql } from '@apollo/client'
import { useMutation, useQuery } from '@apollo/client/react'
import { formatISO } from 'date-fns'
import sanitizeTagCategories from 'helpers/sanitizeTagCategories'

import ActionContainer from 'js/components/ActionContainer/ActionContainer'
import CustomTagsField from 'js/components/CustomTagsField'
import ConfirmAccessModal from 'js/components/EntityActions/components/ConfirmAccessModal'
import RequiredFieldsMessage from 'js/components/EntityActions/components/RequiredFieldsMessage'
import InputLanguage from 'js/components/EntityActions/General/components/InputLanguage'
import Errors from 'js/components/Errors'
import { sanitizeAccessControl } from 'js/components/Form/AccessControlField'
import FormItem from 'js/components/Form/FormItem'
import { Container } from 'js/components/Grid'
import HideVisually from 'js/components/HideVisually/HideVisually'
import Setting from 'js/components/Setting'
import Spacer from 'js/components/Spacer/Spacer'
import TagCategoriesField from 'js/components/TagCategories/TagCategoriesField'
import { NotFound } from 'js/core/NotFound'
import useCustomTags from 'js/page/Widget/hooks/useCustomTags'

import { ADMIN_PAGES } from '../PageList.queries'

import TitleSlugField from './components/TitleSlugField'

const AddEditForm = ({ title, entity, containerGuid, group }) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const location = useLocation()

    const [mutationErrors, setErrors] = useState()

    const [showAccessModal, setShowAccessModal] = useState(false)

    const [categoryTags, setCategoryTags] = useState(
        entity?.tagCategories || [],
    )

    const { customTags, handleAddTags, handleRemoveTags } = useCustomTags(
        entity?.tags,
    )

    const defaultAccessIds = {
        1: 'loggedIn',
        2: 'public',
        4: 'group',
    }
    const defaultGroupAccessId = group?.defaultReadAccessId
    const defaultGroupAccessType =
        defaultGroupAccessId && defaultAccessIds[defaultGroupAccessId]

    const [accessControl, setAccessControl] = useState(
        entity?.accessControl ||
            (defaultGroupAccessType
                ? [
                      {
                          type: defaultGroupAccessType,
                          grant: 'read',
                      },
                  ]
                : []),
    )

    const defaultValues = {
        title: entity?.title || '',
        urlSlug: entity?.urlSlug || '',
        pageType: entity?.pageType || (containerGuid ? 'text' : 'campagne'),
        richDescription: entity?.richDescription || null,
        isDraft: entity?.statusPublished === 'draft' || false,
        isRecommendedInSearch: entity?.isRecommendedInSearch || false,
        isTranslationEnabled: entity?.isTranslationEnabled || false,
        inputLanguage: entity?.inputLanguage || null,
    }

    const formMethods = useForm({
        mode: 'onChange',
        defaultValues,
    })

    const {
        control,
        handleSubmit,
        formState: { errors, isValid, isSubmitting },
        setValue,
        getValues,
        resetField,
        watch,
    } = formMethods

    const [mutate] = useMutation<any>(
        entity ? EDIT_PAGE_VIA_FORM : ADD_PAGE_VIA_FORM,
    )

    const onSubmit = async ({
        title,
        urlSlug,
        pageType,
        richDescription,
        isDraft,
        isRecommendedInSearch,
        isTranslationEnabled,
        inputLanguage,
    }) => {
        const timePublished = entity?.timePublished
            ? entity.timePublished
            : isDraft
              ? null
              : formatISO(new Date())

        const groupGuid = group?.guid

        const input = {
            ...(entity
                ? { guid: entity.guid }
                : {
                      pageType: group
                          ? 'campagne'
                          : containerGuid
                            ? 'text'
                            : pageType,
                  }),
            ...(urlSlugEnabled ? { urlSlug } : {}),
            ...(containerGuid ? { containerGuid } : {}),
            ...(groupGuid ? { groupGuid } : {}),
            title,
            richDescription,
            accessControl: sanitizeAccessControl(accessControl),
            tagCategories: sanitizeTagCategories(
                site.tagCategories,
                categoryTags,
            ),
            tags: Array.from(customTags),
            timePublished,
            isRecommendedInSearch,
            isTranslationEnabled,
            inputLanguage,
        }

        setErrors(null)

        await mutate({
            variables: {
                input,
            },
            refetchQueries: [ADMIN_PAGES],
        })
            .then(({ data }) => {
                if (entity) {
                    // If the slug changed, the previous pathname is now stale (404),
                    // so always navigate to the freshly computed url in that case.
                    const urlSlugChanged =
                        urlSlugEnabled && urlSlug !== entity.urlSlug

                    navigate(
                        urlSlugChanged
                            ? data.editPage.entity.url
                            : location?.state?.prevPathname ||
                                  data.editPage.entity.url,
                        {
                            replace: true,
                        },
                    )
                } else {
                    if (data.addPage.entity.pageType === 'text') {
                        navigate(data.addPage.entity.url, {
                            replace: true,
                        })
                    } else {
                        navigate(`/page/edit/${data.addPage.entity.guid}`, {
                            state: {
                                prevPathname: location?.state?.prevPathname,
                            },
                            replace: true,
                        })
                    }
                }
            })
            .catch((errors) => {
                console.error(errors)
                setErrors(errors)
            })
    }

    const onError = (errors) => {
        return new Promise((_resolve, reject) => {
            reject(new Error(errors))
        })
    }

    const submitForm = handleSubmit(onSubmit, onError)

    const { data, error, loading } = useQuery<any>(GET_SITE)

    const handleChangePageType = (evt: ChangeEvent<HTMLInputElement>) => {
        if (getValues('richDescription')) {
            if (confirm(t('page.discard-unsaved-changes'))) {
                resetField('richDescription')
            }
        }
        setValue('pageType', evt.target.value)
    }

    const handleClose = () => {
        navigate(
            location?.state?.prevPathname ||
                (entity && entity.url) ||
                (group ? `${group.url}/pages` : '/admin/pages'),
            { replace: true },
        )
    }

    if (error) {
        return <NotFound />
    }
    if (loading) {
        return null
    }

    const { site, viewer } = data

    const isPublished = entity?.statusPublished === 'published'

    const watchIsTranslationEnabled = watch('isTranslationEnabled')
    const watchPageType = watch('pageType')

    const urlSlugEnabled = site.editUrlSlugEnabled && !group

    return (
        <ActionContainer
            onDelete={null}
            title={title}
            publish={() => {
                if (isPublished) {
                    setValue('isDraft', false)
                    submitForm()
                } else {
                    setShowAccessModal(true)
                }
            }}
            onClose={handleClose}
            isValid={isValid}
            isPublished={isPublished}
            canSaveAsDraft={true}
            saveAsDraft={() => {
                setValue('isDraft', true)
                submitForm()
            }}
            isSavingAsDraft={isSubmitting && getValues('isDraft')}
            isPublishing={isSubmitting && !getValues('isDraft')}
        >
            <FormProvider {...formMethods}>
                <form>
                    <HideVisually aria-hidden>
                        <FormItem
                            control={control}
                            type="switch"
                            name="isDraft"
                            tabIndex="-1"
                        />
                    </HideVisually>

                    <Container size="small">
                        <Spacer>
                            <Errors errors={mutationErrors} />

                            <TitleSlugField
                                entity={entity}
                                urlSlugEnabled={urlSlugEnabled}
                            />

                            {!entity && !containerGuid && !group ? (
                                <FormItem
                                    type="radio"
                                    control={control}
                                    name="pageType"
                                    size="normal"
                                    legend={t('global.type')}
                                    options={[
                                        {
                                            value: 'campagne',
                                            label: t('page.type-widget'),
                                        },
                                        {
                                            value: 'text',
                                            label: t('page.type-text'),
                                        },
                                    ]}
                                    required
                                    onChange={handleChangePageType}
                                />
                            ) : null}

                            <FormItem
                                key={watchPageType} // force rerender on pageType update
                                control={control}
                                type="rich"
                                name="richDescription"
                                title={t('form.description')}
                                isLargeEditor={true}
                                options={{
                                    textFormat: true,
                                    textStyle: true,
                                    textLink: true,
                                    textQuote: true,
                                    textList: true,
                                    insertMedia: true,
                                    insertAccordion: true,
                                    insertButton: watchPageType === 'text',
                                    insertTable: true,
                                    textAnchor: true,
                                    textCSSClass: true,
                                    textLanguage: true,
                                }}
                                errors={errors}
                            />

                            <InputLanguage
                                control={control}
                                languageOptions={site.languageOptions}
                                showInputLanguage={watchIsTranslationEnabled}
                            />

                            <TagCategoriesField
                                tagCategories={site.tagCategories}
                                value={categoryTags}
                                setTags={setCategoryTags}
                            />

                            {site.customTagsAllowed && (
                                <CustomTagsField
                                    name="customTags"
                                    value={customTags}
                                    onAddTag={handleAddTags}
                                    onRemoveTag={handleRemoveTags}
                                />
                            )}

                            {viewer?.isAdmin && (
                                <Setting
                                    subtitle="form.recommended-search-result"
                                    helper="form.recommended-search-result-helper"
                                    htmlFor="isRecommendedInSearch"
                                >
                                    <FormItem
                                        control={control}
                                        type="switch"
                                        name="isRecommendedInSearch"
                                        size="small"
                                    />
                                </Setting>
                            )}

                            <RequiredFieldsMessage />
                        </Spacer>
                    </Container>
                </form>
            </FormProvider>

            <ConfirmAccessModal
                showModal={showAccessModal}
                onConfirm={() => {
                    setShowAccessModal(false)
                    setValue('isDraft', false)
                    submitForm()
                }}
                onCancel={() => setShowAccessModal(false)}
                accessControl={accessControl}
                setAccessControl={setAccessControl}
                group={group}
            />
        </ActionContainer>
    )
}

const GET_SITE = gql`
    query GetSite {
        site {
            guid
            customTagsAllowed
            tagCategories {
                name
                values
            }
            languageOptions {
                value
                label
            }
            editUrlSlugEnabled
        }
        viewer {
            guid
            isAdmin
        }
    }
`

const ADD_PAGE_VIA_FORM = gql`
    mutation AddPageViaForm($input: addPageInput!) {
        addPage(input: $input) {
            entity {
                guid
                ... on Page {
                    url
                    pageType
                }
            }
        }
    }
`

const EDIT_PAGE_VIA_FORM = gql`
    mutation EditPageViaForm($input: editPageInput!) {
        editPage(input: $input) {
            entity {
                guid
                ... on Page {
                    url
                }
            }
        }
    }
`

export default AddEditForm
